import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingAuthContext = createContext(undefined);

// Helper: build headers with Bearer token if available
const getAuthHeaders = (extra = {}) => {
  const headers = { ...extra };
  const token = localStorage.getItem('auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Helper: detect cross-origin
const isCrossOrigin = (() => {
  try {
    return new URL(API_URL).origin !== window.location.origin;
  } catch {
    return false;
  }
})();

export const useLandingAuth = () => {
  const context = useContext(LandingAuthContext);
  if (context === undefined) {
    throw new Error('useLandingAuth must be used within a LandingAuthProvider');
  }
  return context;
};

export const LandingAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const initAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: getAuthHeaders(),
        credentials: isCrossOrigin ? 'omit' : 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser({ email: userData.email, id: userData.id });
        setProfile({
          id: userData.id,
          email: userData.email,
          fullName: userData.name,
          subscriptionTier: 'free'
        });
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const signUp = async (email, password, fullName) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: isCrossOrigin ? 'omit' : 'include',
        body: JSON.stringify({ email, password, name: fullName })
      });

      const data = await response.json();

      if (!response.ok) {
        return { error: { message: data.detail || 'Failed to create account' } };
      }

      // Store token for cross-origin auth
      if (data.access_token) {
        localStorage.setItem('auth_token', data.access_token);
      }

      return { error: null };
    } catch (error) {
      return { error: { message: error.message || 'An unexpected error occurred' } };
    }
  };

  const signIn = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: isCrossOrigin ? 'omit' : 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        return { error: { message: data.detail || 'Invalid email or password' } };
      }

      // Store token for cross-origin auth
      if (data.access_token) {
        localStorage.setItem('auth_token', data.access_token);
      }

      setUser({ email: data.user.email, id: data.user.id });
      setProfile({
        id: data.user.id,
        email: data.user.email,
        fullName: data.user.name,
        subscriptionTier: 'free'
      });

      return { error: null };
    } catch (error) {
      return { error: { message: error.message || 'An unexpected error occurred' } };
    }
  };

  const signOut = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: isCrossOrigin ? 'omit' : 'include'
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('auth_token');
    setUser(null);
    setProfile(null);
  };

  const value = {
    user,
    profile,
    isLoading,
    signUp,
    signIn,
    signOut
  };

  return (
    <LandingAuthContext.Provider value={value}>
      {children}
    </LandingAuthContext.Provider>
  );
};

export default LandingAuthContext;
